import { dataSourceItem } from "../../redux/models/reduxModels";
import { ClientTypeLookup } from "../../services/clientTypeQuery";
import { DatasourceTypeEnum } from "../enums/datasourceTypeEnum";

/**
 * How the client-type dimension should be evaluated for the locations in scope.
 *
 * Three modes are needed because the "empty array means skip" convention the site and library
 * arguments use cannot express the difference between "we do not know this site's type"
 * (inert) and "this site genuinely has no type" (must fail an allow-list).
 *
 * - inert             - not evaluated: feature off, lookup failed, or no location in scope.
 * - resolved          - types of the sites in scope; an EMPTY array means "no client row".
 * - excludeRestricted - Advanced Search in multi-site-collection mode. A per-location rule has
 *                       no correct answer across a set of locations, so any datasource carrying
 *                       a client-type restriction is excluded rather than guessed at.
 */
export type ClientTypeContext =
  | { mode: "inert" }
  | { mode: "resolved"; types: string[] }
  | { mode: "excludeRestricted" };

export const INERT_CLIENT_TYPES: ClientTypeContext = Object.freeze({ mode: "inert" });

export const EXCLUDE_RESTRICTED_CLIENT_TYPES: ClientTypeContext = Object.freeze({ mode: "excludeRestricted" });

// Contexts derived from the same lookup keep the same identity. Load-bearing for React: the
// lookup objects themselves are session-cached singletons (clientTypeService), so memoizing on
// them makes every `[clientTypes]` dependency array stable across renders - Advanced Search
// derives its context per render, and without this each render would hand every datasource
// input a fresh object and re-run its resolution memo.
const contextByLookup: WeakMap<ClientTypeLookup, ClientTypeContext> = new WeakMap<ClientTypeLookup, ClientTypeContext>();

export const toClientTypeContext = (lookup?: ClientTypeLookup): ClientTypeContext => {
  if (!lookup || lookup.state === "unknown") {
    return INERT_CLIENT_TYPES;
  }
  const cached: ClientTypeContext | undefined = contextByLookup.get(lookup);
  if (cached) {
    return cached;
  }
  const context: ClientTypeContext = lookup.state === "known"
    ? Object.freeze({ mode: "resolved" as const, types: [lookup.clientType] })
    : Object.freeze({ mode: "resolved" as const, types: [] as string[] });
  contextByLookup.set(lookup, context);
  return context;
};

const normalize = (value?: string): string => value?.trim().toUpperCase() ?? "";

// readonly so the compiler forbids mutation (the freeze alone only fails at runtime).
const EMPTY_TYPES: readonly string[] = Object.freeze([]);

// Blank values are excluded while building the scope set - a restriction entry only matches a
// non-blank value in scope. This guard comes from the Advanced Search copy of this function,
// and adopting it here is a deliberate normalization that changes BOTH directions of the old
// taskpane behavior when the scope value is blank (no directory display name): a restriction
// ending in a stray ";" used to match the blank scope - granting access on the allow path AND
// blocking on the blacklist path. Both flips are intended (a stray separator is a typo, not a
// rule about blank-named sites) and both are pinned by tests. NOTE: the all-blank-entries
// normalization below (hasRestrictionEntries: ";" counts as no restriction) applies to the
// CLIENT-TYPE columns only - a site/library column of ";" keeps its historical behavior
// (an active restriction that matches nothing).
const toNormalizedScope = (valuesInScope: readonly string[]): Set<string> => {
  const scope: Set<string> = new Set<string>();
  for (const value of valuesInScope) {
    const normalized: string = normalize(value);
    if (normalized) {
      scope.add(normalized);
    }
  }
  return scope;
};

// Set lookup instead of a nested some: E entries x V scope values used to cost E*V
// trim/uppercase allocations per call; the set costs V once (memoized per evaluation via
// ScopeRef) and each entry one normalize + one O(1) has. An EMPTY set (all scope values
// blank) matches nothing - allow-lists block, blacklists stay silent - exactly the nested
// loop's behavior for a blank-only scope.
const matchesScope = (entries: string[], scope: Set<string>): boolean => {
  if (scope.size === 0) {
    return false;
  }
  return entries.some(entry => {
    const normalized: string = normalize(entry);
    return normalized !== "" && scope.has(normalized);
  });
};

const hasNonBlankEntry = (entries: string[]): boolean => entries.some(entry => entry.trim().length > 0);

/** True when the restriction string carries at least one non-blank entry. `";"` or `" ; "`
 * normalizes to "no restriction" - an admin typo must not hide a datasource from everyone. */
export const hasRestrictionEntries = (restriction?: string): boolean =>
  !!restriction && hasNonBlankEntry(restriction.split(";"));

/** The raw scope values plus a lazily built, memoized normalized set: the set is only paid
 * for when a restriction actually consults the dimension, and getDatasourceFields shares one
 * ref across every datasource so the whole filter pass normalizes each scope at most once. */
interface IScopeRef {
  values: string[];
  active: boolean;
  set?: Set<string>;
}

const scopeRef = (values: string[]): IScopeRef =>
  ({ values, active: Array.isArray(values) && values.length > 0 });

const scopeSet = (ref: IScopeRef): Set<string> =>
  ref.set ?? (ref.set = toNormalizedScope(ref.values));

export class DatasourceFieldHelper {
  static checkDatasourceAllowed(
    sites: string[],
    libraries: string[],
    datasource: dataSourceItem,
    clientTypes: ClientTypeContext = INERT_CLIENT_TYPES,
  ) {
    return DatasourceFieldHelper.checkWithScopes(scopeRef(sites), scopeRef(libraries), datasource, clientTypes);
  }

  private static checkWithScopes(
    siteScope: IScopeRef,
    libraryScope: IScopeRef,
    datasource: dataSourceItem,
    clientTypes: ClientTypeContext,
  ): boolean {
    let siteAllowed: boolean = true;
    let libraryAllowed: boolean = true;
    let clientTypeAllowed: boolean = true;

    // `active` mirrors the original `Array.isArray(x) && x.length > 0` gate on the RAW value
    // count - a scope of only blank values stays active (its empty set then matches nothing,
    // so allow-lists block), while an empty/absent scope skips the dimension entirely.
    if (datasource?.site
      && siteScope.active
      && !matchesScope(datasource.site.split(";"), scopeSet(siteScope))) {
      siteAllowed = false;
    }

    if (siteAllowed
      && datasource?.library
      && libraryScope.active
      && !matchesScope(datasource.library.split(";"), scopeSet(libraryScope))) {
      libraryAllowed = false;
    }

    if (siteAllowed && libraryAllowed) {
      if (datasource?.siteBlacklist
        && siteScope.active
        && matchesScope(datasource.siteBlacklist.split(";"), scopeSet(siteScope))) {
        siteAllowed = false;
      }

      if (siteAllowed
        && datasource?.libraryBlacklist
        && libraryScope.active
        && matchesScope(datasource.libraryBlacklist.split(";"), scopeSet(libraryScope))) {
        libraryAllowed = false;
      }
    }

    // Nullish contexts collapse to inert here (the parameter default only covers undefined,
    // not an explicit null from a JS caller) so the block below can dereference freely.
    const context: ClientTypeContext = clientTypes ?? INERT_CLIENT_TYPES;
    if (siteAllowed && libraryAllowed && context.mode !== "inert") {
      // Split ONCE per column and reuse for both the activeness test and the match. A blank
      // or all-blank-entries column ("", "   ", ";") is no restriction in every mode.
      const allowEntries: string[] | undefined = datasource?.clientType ? datasource.clientType.split(";") : undefined;
      const denyEntries: string[] | undefined = datasource?.clientTypeBlacklist ? datasource.clientTypeBlacklist.split(";") : undefined;
      const hasAllow: boolean = !!allowEntries && hasNonBlankEntry(allowEntries);
      const hasDeny: boolean = !!denyEntries && hasNonBlankEntry(denyEntries);

      if (hasAllow || hasDeny) {
        if (context.mode === "excludeRestricted") {
          clientTypeAllowed = false;
        } else {
          const typeScope: Set<string> = toNormalizedScope(context.types ?? EMPTY_TYPES);
          if (hasAllow && !matchesScope(allowEntries as string[], typeScope)) {
            clientTypeAllowed = false;
          }
          if (clientTypeAllowed && hasDeny && matchesScope(denyEntries as string[], typeScope)) {
            clientTypeAllowed = false;
          }
        }
      }
    }

    return (siteAllowed && libraryAllowed && clientTypeAllowed);
  }

  static getDatasourceFields(
    datasources: dataSourceItem[],
    sites: string[],
    libraries: string[],
    clientTypes: ClientTypeContext = INERT_CLIENT_TYPES,
  ) {
    // ONE scope ref pair for the whole pass: every datasource shares the memoized normalized
    // sets. One output array: the old map + reduce-concat re-allocated the accumulator per
    // datasource (quadratic in total field count).
    const siteScope: IScopeRef = scopeRef(sites);
    const libraryScope: IScopeRef = scopeRef(libraries);
    const fields: dataSourceItem["boundFields"] = [];
    for (const item of datasources) {
      if (item?.type?.id !== DatasourceTypeEnum.DocubirdManagedSites
        && item?.type?.id !== DatasourceTypeEnum.SharePointDocumentTemplate
        && DatasourceFieldHelper.checkWithScopes(siteScope, libraryScope, item, clientTypes)
        && Array.isArray(item?.boundFields)) {
        fields.push(...item.boundFields);
      }
    }
    return fields;
  }
}
